import React, { useRef, useEffect } from "react";
import { formatIndianNumber } from "../../_helpers/formatUtils";

interface PriceRangeFilterProps {
  minPrice: number;
  maxPrice: number;
  selectedRange: { min: number; max: number };
  onChange: (range: { min: number; max: number }) => void;
  label: string;
}

export const PriceRangeFilter: React.FC<PriceRangeFilterProps> = ({
  minPrice,
  maxPrice,
  selectedRange,
  onChange,
  label,
}) => {
  const sliderRef = useRef<HTMLDivElement>(null);

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMin = Math.min(
      Math.max(Number(e.target.value), minPrice),
      selectedRange.max - 0.01
    );
    onChange({ min: newMin, max: selectedRange.max });
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMax = Math.max(
      Math.min(Number(e.target.value), maxPrice),
      selectedRange.min + 0.01
    );
    onChange({ min: selectedRange.min, max: newMax });
  };

  useEffect(() => {
    if (sliderRef.current) {
      const minPercent =
        ((selectedRange.min - minPrice) / (maxPrice - minPrice)) * 100;
      const maxPercent =
        ((selectedRange.max - minPrice) / (maxPrice - minPrice)) * 100;

      sliderRef.current.style.setProperty("--min-percent", `${minPercent}%`);
      sliderRef.current.style.setProperty("--max-percent", `${maxPercent}%`);

      const distance = maxPercent - minPercent;
      if (distance < 5) {
        sliderRef.current.classList.add("sliders-close");
      } else {
        sliderRef.current.classList.remove("sliders-close");
      }
    }
  }, [selectedRange, minPrice, maxPrice]);

  return (
    <div className="price-range-filter-section">
      <h5 className="mb-2">{label}</h5>
      <div className="price-range-controls">
        <div className="price-range-slider-container" ref={sliderRef}>
          <div className="price-range-labels">
            <span>₹ {formatIndianNumber(minPrice)}</span>
            <span>₹ {formatIndianNumber(maxPrice)}</span>
          </div>
          <div className="price-range-slider">
            <input
              type="range"
              min={minPrice}
              max={maxPrice}
              value={selectedRange.min}
              onChange={handleMinChange}
              className="slider min"
              step="0.01"
            />
            <input
              type="range"
              min={minPrice}
              max={maxPrice}
              value={selectedRange.max}
              onChange={handleMaxChange}
              className="slider max"
              step="0.01"
            />
          </div>
          <div className="price-range-selected">
            ₹ {formatIndianNumber(selectedRange.min)} - ₹{" "}
            {formatIndianNumber(selectedRange.max)}
          </div>
        </div>
      </div>
    </div>
  );
};
